import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { DEFAULT_PRINTING_SETTINGS, normalizeSettings } from '../lib/printing';

const SettingsContext = createContext(null);

const DEFAULT_SETTINGS = {
  storeName: 'ValenPOS',
  ticketDisplayName: 'Mi Tienda POS',
  currency: 'COP',
  taxEnabled: true,
  taxRate: 19,
  address: 'Calle Falsa 123, Ciudad',
  phone: '+57 000 000 0000',
  email: 'contacto@mitienda.com',
  storeLogo: '',
  globalNoticeEnabled: false,
  globalNoticeTitle: '',
  globalNoticeMessage: '',
  globalNoticeUpdatedBy: '',
  globalNoticeUpdatedAt: '',
  printing: DEFAULT_PRINTING_SETTINGS,
};

const SETTINGS_DOC_PATH = ['settings', 'global'];

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(normalizeSettings(DEFAULT_SETTINGS));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const docRef = useMemo(() => doc(db, ...SETTINGS_DOC_PATH), []);

  useEffect(() => {
    let mounted = true;
    const fetchSettings = async () => {
      try {
        const snap = await getDoc(docRef);
        if (snap.exists() && mounted) {
          setSettings((prev) => normalizeSettings({ ...prev, ...snap.data() }));
        }
      } catch (error) {
        console.error('No se pudo cargar la configuracion global:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!mounted) return;
      if (!user) {
        setLoading(false);
        return;
      }
      fetchSettings();
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [docRef]);

  const saveSettings = async (nextSettings) => {
    setSaving(true);
    try {
      const payload = normalizeSettings({
        ...settings,
        ...nextSettings,
        printing: {
          ...settings?.printing,
          ...nextSettings?.printing,
        },
      });
      await setDoc(docRef, payload, { merge: true });
      setSettings(payload);
    } catch (error) {
      console.error('No se pudo guardar la configuracion:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loadingSettings: loading,
        savingSettings: saving,
        saveSettings,
        setSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error('useSettings debe usarse dentro de SettingsProvider');
  }
  return ctx;
};
